import { Controller } from '@hotwired/stimulus'
import {
  Chart,
  ArcElement,
  BarElement,
  BarController,
  PieController,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  Filler,
  Legend,
  Title,
} from 'chart.js';

Chart.register(
  ArcElement,
  BarElement,
  BarController,
  PieController,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  Filler,
  Legend,
  Title,
);

import pattern from 'patternomaly'

const CHART_COLORS = '556270 4ecec4 c7f464 ff6b6b c44d58 ff8002 ff5c44 ff3f6f e63b95 b94ab2 7b58c2 0060c0'.split(' ').map(c => `#${c}`)
const CHART_PATTERNS_RANKED_BY_PREFERENCE = [
  'line-vertical',
  'zigzag',
  'zigzag-vertical',
  'diagonal',
  'diagonal-right-left',
  'line',
  'dot',
  'plus',
  'cross',
  'square',
  'dash',
  'cross-dash',
  'weave',
  'triangle',
  'triangle-inverted',
  'diamond',
  'box',
  'diamond-box',
  'dot-dash',
  'disc',
  'ring',
]

const PATTERNS_AND_COLORS = CHART_PATTERNS_RANKED_BY_PREFERENCE.reduce(
  (acc, patternName, index) => {
    const patternColor = CHART_COLORS[index % CHART_COLORS.length]
    acc.push(pattern.draw(patternName, patternColor))

    return acc
  }, []
)

const HORIZONTAL_BAR_LABEL_PREVIEW_LENGTH = 43
const HORIZONTAL_BAR_LABEL_LINE_LENGTH = 65
const HORIZONTAL_BAR_MIN_HEIGHT = 350
const HORIZONTAL_BAR_TOP_BOTTOM_PADDING = 90
const HORIZONTAL_BAR_FULL_LABEL_FONT_SIZE = 14
const HORIZONTAL_BAR_FULL_LABEL_LINE_HEIGHT = 18
const HORIZONTAL_BAR_ROW_PADDING = 14
const HORIZONTAL_BAR_FULL_LABEL_AXIS_MIN_WIDTH = 360
const HORIZONTAL_BAR_FULL_LABEL_AXIS_MAX_WIDTH_RATIO = 0.5

const Y_AXIS_INTEGER_SCALE = {
  y: {
    beginAtZero: true,
    ticks: {
      stepSize: 1
    }
  }
}

export default class extends Controller {
  static targets = ['canvas', 'fullLabelsButton']
  initialize() {
    Chart.defaults.font.family = 'Lato'
    Chart.defaults.font.size = 20
  }

  connect() {
    window.EB.charts = window.EB.charts || []
    window.EB.charts.push(this)
    this.chartNode = this.canvasTargets[0]
    this.chartData = JSON.parse(this.chartNode.dataset['chartData'])
    const chartData = this.chartData

    this.chartType = this.chartNode.dataset['chartType']
    this.showFullHorizontalBarLabels = false

    if (!['bar', 'pie', 'horizontalBar'].includes(this.chartType)) {
      throw new Error("Unexpected chart type: " + this.chartType)
    }

    const chartOptions = this.constructor.generateChartOptions(
      this.chartType,
      chartData,
      this.showFullHorizontalBarLabels
    )

    this.setHorizontalBarHeight()
    this.updateFullLabelsButton()

    this.chartArgs = {
      type: this.chartType != 'pie' ? 'bar' : 'pie',
      data: chartData,
      options: chartOptions
    }
    this.setBackground(this.chartNode.dataset.chartPatterns === 'true')
    this.setScale()
    this.chart = new Chart(this.chartNode, this.chartArgs)
  }

  setBackground(patterned) {
    const POOL_TO_DRAW_FROM = patterned ? PATTERNS_AND_COLORS : [...CHART_COLORS, ...PATTERNS_AND_COLORS]

    if (this.chartData.is_scored_survey) {
      this.chartData.datasets.forEach((set) => {
        const background = set.data.map(() => POOL_TO_DRAW_FROM[(set.score - 1) % POOL_TO_DRAW_FROM.length])
        set.backgroundColor = background
      })
    } else {
      const colors = this.chartData.datasets[0].data.map(
        (_set, index) => POOL_TO_DRAW_FROM[index % POOL_TO_DRAW_FROM.length]
      )

      this.chartData.datasets.forEach((dataSet) => {
        dataSet.backgroundColor = colors
      })
    }
  }

  setScale() {
    if (this.chartType === 'horizontalBar') return

    this.chartArgs.options.scales = this.chartType === 'bar' ? Y_AXIS_INTEGER_SCALE : {}
  }

  setHorizontalBarHeight() {
    if (this.chartType !== 'horizontalBar') return

    this.chartNode.parentElement.style.height = `${this.constructor.horizontalBarHeight(
      this.chartData,
      this.showFullHorizontalBarLabels
    )}px`
  }

  toggleFullLabels(event) {
    event.preventDefault()

    if (this.chartType !== 'horizontalBar') return

    this.showFullHorizontalBarLabels = !this.showFullHorizontalBarLabels
    this.chartArgs.options = this.constructor.generateChartOptions(
      this.chartType,
      this.chartData,
      this.showFullHorizontalBarLabels
    )
    this.chart.options = this.chartArgs.options
    this.setHorizontalBarHeight()
    this.updateFullLabelsButton()
    this.chart.update()
  }

  updateFullLabelsButton() {
    if (!this.hasFullLabelsButtonTarget) return

    const button = this.fullLabelsButtonTarget
    button.textContent = this.showFullHorizontalBarLabels ? button.dataset.hideLabel : button.dataset.showLabel
    button.setAttribute('aria-pressed', this.showFullHorizontalBarLabels.toString())
  }

  redraw(change) {
    this.chart.reset()
    if (change.type && this.chartArgs.type !== 'horizontalBar') {
      this.chartType = change.type
      this.chartArgs.type = change.type
      this.setScale()
    }

    if (typeof change.patterns == 'boolean') {
      this.setBackground(change.patterns)
    }
    this.chart.update(this.chartArgs)
    this.chart.render()
  }

  static generateLegendOptions(chartType, chartData) {
    return {
      display: chartType !== 'bar' && !chartData.displays_average_scores,
      labels: {
        padding: chartData.labels?.length > 8 ? 15 : 10,
      }
    }
  }

  static truncateLabel(label, maxLength = HORIZONTAL_BAR_LABEL_PREVIEW_LENGTH) {
    const stringLabel = String(label || '')

    if (stringLabel.length <= maxLength) return stringLabel

    return `${stringLabel.slice(0, maxLength)}...`
  }

  static wrapLabel(label, maxLength = HORIZONTAL_BAR_LABEL_LINE_LENGTH) {
    const stringLabel = String(label || '')

    if (stringLabel.length <= maxLength) return stringLabel

    const lines = []
    let currentLine = ''

    stringLabel.split(/\s+/).forEach((word) => {
      const wordChunks = this.splitLongWord(word, maxLength)

      wordChunks.forEach((chunk) => {
        if (!currentLine) {
          currentLine = chunk
        } else if (`${currentLine} ${chunk}`.length <= maxLength) {
          currentLine = `${currentLine} ${chunk}`
        } else {
          lines.push(currentLine)
          currentLine = chunk
        }
      })
    })

    if (currentLine) lines.push(currentLine)

    return lines
  }

  static splitLongWord(word, maxLength) {
    if (word.length <= maxLength) return [word]

    const chunks = []

    for (let index = 0; index < word.length; index += maxLength) {
      chunks.push(word.slice(index, index + maxLength))
    }

    return chunks
  }

  static previewLabels(chartData) {
    return (chartData.labels || []).map((label) => this.truncateLabel(label))
  }

  static fullLabels(chartData) {
    return (chartData.labels || []).map((label) => this.wrapLabel(label))
  }

  static horizontalBarHeight(chartData, showFullLabels = false) {
    if (!showFullLabels) return HORIZONTAL_BAR_MIN_HEIGHT

    const wrappedLabels = this.fullLabels(chartData)
    const maxLineCount = Math.max(
      ...wrappedLabels.map((label) => (Array.isArray(label) ? label.length : 1)),
      1
    )
    const rowHeight = (HORIZONTAL_BAR_FULL_LABEL_LINE_HEIGHT * maxLineCount) + HORIZONTAL_BAR_ROW_PADDING

    return Math.max(
      HORIZONTAL_BAR_MIN_HEIGHT,
      HORIZONTAL_BAR_TOP_BOTTOM_PADDING + (wrappedLabels.length * rowHeight)
    )
  }

  static generateHorizontalBarScaleOptions(chartData, showFullLabels = false) {
    const labels = showFullLabels ? this.fullLabels(chartData) : this.previewLabels(chartData)
    const tickOptions = {
      callback: (value) => labels[value] || value,
    }
    const scaleOptions = {
      ticks: tickOptions,
    }

    if (showFullLabels) {
      tickOptions.font = {
        size: HORIZONTAL_BAR_FULL_LABEL_FONT_SIZE,
      }
      scaleOptions.afterFit = (scale) => {
        const maximumWidth = scale.chart.width * HORIZONTAL_BAR_FULL_LABEL_AXIS_MAX_WIDTH_RATIO
        scale.width = Math.min(
          Math.max(scale.width, HORIZONTAL_BAR_FULL_LABEL_AXIS_MIN_WIDTH),
          maximumWidth
        )
      }
    }

    return {
      y: scaleOptions,
    }
  }

  static generateChartOptions(chartType, chartData, showFullLabels = false) {
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: this.generateLegendOptions(chartType, chartData),
      },
    }

    if (chartData.scored) {
      if (chartData.datasets.length === 1) {
        chartOptions.legend = { display: false }
      }
      chartOptions.scales = {
        xAxes: [{ stacked: true }],
        yAxes: [{ stacked: true }],
      }
    }

    if (chartType === 'horizontalBar') {
      chartOptions.indexAxis = 'y'
      chartOptions.scales = {
        ...chartOptions.scales,
        ...this.generateHorizontalBarScaleOptions(chartData, showFullLabels),
      }
    }

    if (chartData.displays_average_scores) {
      chartOptions.plugins = chartOptions.plugins || {}
      chartOptions.plugins.title = {
        text: (window.I18n && window.I18n.exists && window.I18n.exists('pages.vote.results.question.average_score'))
          ? window.I18n.t('pages.vote.results.question.average_score')
          : 'Average Score',
        display: true,
      }
    }

    return chartOptions
  }
}
